import styled from 'styled-components';
import { useApolloClient, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Info } from '@material-ui/icons';
import { Button, Modal } from '@avangard/ui/core';
import { black, orange } from '@avangard/ui/colors';

import { CENTER_DOMAIN } from '@config/environment';
import { routes } from '@config/routes';
import { useNavigate } from '@lib/routing';
import { useEnqueueStacks } from '@modules/layout/hooks';
import { SetCurrentLapMutation } from '@modules/lap/graphql';
import { Platform } from '@modules/types/graphql';

import type { LapEntity } from '@modules/lap/entities';
import type {
    SetCurrentLapMutationType,
    SetCurrentLapMutationVariables,
} from '@modules/types/graphql';

type HandbookChangeLapModalProps = {
    lap: LapEntity | null;
    onClose: () => void;
};

const Header = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    width: 100%;
    padding-top: 16px;
    line-height: 22px;
`;

const Icon = styled.div`
    display: flex;
    width: 24px;
    height: 24px;
    color: ${orange[50]};
`;

const Title = styled.div`
    width: calc(100% - 32px);
    margin-inline-start: 8px;
    color: ${black[100]};
    font-size: 1.6rem;
    line-height: 26px;
`;

const Body = styled.div`
    width: 100%;
    margin-top: 8px;
    padding-inline-start: 32px;
    font-size: 1.4rem;
    line-height: 22px;
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    margin-top: 24px;

    > * {
        &:not(:last-of-type) {
            margin-inline-end: 8px;
        }
    }
`;

const infoIcon = <Info style={{ fontSize: 24 }} />;

const HandbookChangeLapModal = (props: HandbookChangeLapModalProps) => {
    const { lap, onClose } = props;

    const { t } = useTranslation(['common', 'lap']);

    const navigate = useNavigate();
    const apolloClient = useApolloClient();

    const { enqueueError } = useEnqueueStacks();

    const [setCurrentLap, { loading: setCurrentLapLoading }] = useMutation<
        SetCurrentLapMutationType,
        SetCurrentLapMutationVariables
    >(SetCurrentLapMutation);

    const handleClickChange = async () => {
        try {
            if (!lap) {
                return;
            }

            const { data: setCurrentLapData } = await setCurrentLap({
                variables: { id: lap.id, platform: lap.platform },
            });

            if (!setCurrentLapData?.setCurrentLap) {
                throw new Error();
            }

            if (lap.platform === Platform.online) {
                apolloClient.resetStore();
                navigate(routes.index.path);
                onClose();
            } else {
                window.location.href = CENTER_DOMAIN;
            }
        } catch (e) {
            enqueueError(t('lap:notifiers.change_lap.error'));
        }
    };

    return (
        <Modal open={!!lap} onClose={onClose}>
            <Header>
                <Icon>{infoIcon}</Icon>
                <Title>{t('lap:change_lap.title')}</Title>
            </Header>

            <Body>
                {lap
                    ? t('lap:change_lap.body', {
                          date: lap.getFullDate(),
                          platform: t(`common:blocks.platform.${lap.platform}`),
                      })
                    : null}
            </Body>

            <Actions>
                <Button variant='outlined' disabled={setCurrentLapLoading} onClick={onClose}>
                    {t('comoon:actions.cancel')}
                </Button>

                <Button type='submit' loading={setCurrentLapLoading} onClick={handleClickChange}>
                    {t('lap:actions.change_lap')}
                </Button>
            </Actions>
        </Modal>
    );
};

export { HandbookChangeLapModal };
